import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import './assets/tailwind.css'
import ElementPlus from 'element-plus'
import 'element-plus/dist/index.css'
import axios from 'axios'
import * as Icons from '@element-plus/icons-vue' // 引入所有图标
import 'animate.css'
import { getAuthorization } from './router/auth'
import { ElLoading } from 'element-plus'
import './assets/styles/global.css' // 引入颜色定义

// ==================== 全局 Loading 配置 ====================
// 使用闭包避免全局变量污染
const createLoadingManager = () => {
  let loadingCount = 0
  let loadingInstance = null
  const loadingQueue = [] // 用于存储未完成的请求

  const showLoading = (options = {}) => {
    if (loadingCount === 0) {
      loadingInstance = ElLoading.service({
        lock: true,
        text: options.text || 'Loading...',
        background: options.background || 'rgba(0, 0, 0, 0.7)',
        ...options
      })
    }
    loadingCount++
  }

  const hideLoading = (requestId) => {
    // 如果提供了requestId，从队列中移除
    if (requestId) {
      const index = loadingQueue.indexOf(requestId)
      if (index > -1) {
        loadingQueue.splice(index, 1)
      }
    }
    
    loadingCount = Math.max(0, loadingCount - 1)
    
    if (loadingCount <= 0 && loadingInstance) {
      loadingInstance.close()
      loadingInstance = null
      loadingCount = 0
      loadingQueue.length = 0 // 清空队列
    }
  }

  return { showLoading, hideLoading, loadingQueue }
}

const loadingManager = createLoadingManager()

// ==================== 请求唯一ID生成 ====================
const generateRequestId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2)
}

// ==================== 全局 Axios 配置 ====================
// axios.defaults.baseURL = 'http://192.168.1.104'
// axios.defaults.baseURL = 'http://localhost/'
// axios.defaults.baseURL = 'https://touxiang.jiatong.site'
axios.defaults.baseURL = 'https://api.hk-panel.top'

// 添加请求拦截器
axios.interceptors.request.use(
  (config) => {
    // 添加token
    const token = getAuthorization()
    if (token) {
      config.headers.token = token
    }
    
    // 生成请求唯一ID
    const requestId = generateRequestId()
    config.requestId = requestId
    
    // 默认显示loading，除非明确设置hideLoading为true
    const shouldShowLoading = config.hideLoading !== true
    
    if (shouldShowLoading) {
      // 立即显示loading（移除延迟以避免请求太快时不显示）
      loadingManager.loadingQueue.push(requestId)
      loadingManager.showLoading(config.loadingOptions || {})
      
      // 添加超时保护：10秒后自动清除（防止loading一直显示）
      config.loadingTimeout = setTimeout(() => {
        console.warn(`请求超时，强制清除loading: ${config.url}`)
        loadingManager.hideLoading(requestId)
      }, 10000)
    }
    
    return config
  },
  (error) => {
    // 请求配置错误时，清除可能的loading
    if (error.config?.requestId) {
      clearTimeout(error.config.loadingTimeout)
      loadingManager.hideLoading(error.config.requestId)
    }
    
    // 这里不能用this.$router，因为是axios拦截器，不是Vue实例
    router.push('/login')
    return Promise.reject(error)
  }
)

// 添加响应拦截器
axios.interceptors.response.use(
  (response) => {
    // 正常响应，清除loading
    if (response.config?.requestId) {
      clearTimeout(response.config.loadingTimeout)
      loadingManager.hideLoading(response.config.requestId)
    }
    return response
  },
  (error) => {
    // 错误响应，清除loading
    if (error.config?.requestId) {
      clearTimeout(error.config.loadingTimeout)
      loadingManager.hideLoading(error.config.requestId)
    }
    
    if (error.response?.status === 401) {
      router.push('/login')
    }
    return Promise.reject(error)
  }
)

// ==================== 创建 Vue 应用 ====================
const app = createApp(App)

// 全局注册所有图标
Object.keys(Icons).forEach((key) => {
  app.component(key, Icons[key])
})

// 添加全局loading控制方法
app.config.globalProperties.$loading = {
  show: (options) => loadingManager.showLoading(options),
  hide: () => {
    // 强制隐藏所有loading（紧急情况下使用）
    while (loadingManager.loadingQueue.length > 0) {
      const requestId = loadingManager.loadingQueue.pop()
      clearTimeout() // 清理超时定时器
      console.log(requestId)
    }
    loadingManager.hideLoading()
  }
}

// 挂载到axios实例以便全局访问
axios.$loading = app.config.globalProperties.$loading

// 初始化用户信息（如果 store 没有自动从 localStorage 读取）
const storedUser = localStorage.getItem('user');
if (storedUser) {
  store.commit('user/setUserInfo', JSON.parse(storedUser));
}

// 注册插件
app.use(store)
app.use(router)
app.use(ElementPlus)

// 挂载前设置全局属性
app.config.globalProperties.$axios = axios

app.mount('#app')

// 导出
export { axios, app }